import { useEffect, useMemo, type ReactNode } from 'react';
import { HubClient } from '@adiox/api';
import { useApi, useSession, useSessionContext } from '@adiox/ui';
import { recordTick } from '../chart/tickHistoryStore';
import { StreamContext } from './stream';
import { createStreamStore, type StreamStore } from './streamStore';

/** Opens the live connection while the user is signed in and starts streaming the user's trading accounts. */
export function StreamProvider({ children, store: injected }: { children: ReactNode; store?: StreamStore }) {
  const { tokens } = useSessionContext();
  const api = useApi();
  const session = useSession();
  const accounts = session.user?.accounts;

  const created = useMemo(() => {
    if (injected) return null;
    const hub = new HubClient({ tokens });
    return { hub, store: createStreamStore(hub, api) };
  }, [injected, tokens, api]);
  const store = injected ?? created!.store;

  useEffect(() => {
    if (!created) return;
    void created.hub.start();
    return () => {
      created.store.dispose();
      void created.hub.stop();
    };
  }, [created]);

  // Keeps every watched symbol's tick history running regardless of whether a tick chart is open to show it, so one
  // opened later picks up where the feed is now instead of starting fresh.
  useEffect(
    () =>
      store.subscribe((state, previous) => {
        if (state.quotes === previous.quotes) return;
        for (const [symbol, quote] of Object.entries(state.quotes)) {
          if (quote && quote !== previous.quotes[symbol]) {
            recordTick(symbol, { t: quote.t, bid: quote.bid, ask: quote.ask });
          }
        }
      }),
    [store],
  );

  const key = (accounts ?? []).join(',');
  useEffect(() => {
    const logins = key ? key.split(',').map(Number) : [];
    logins.forEach((login) => void store.watchAccount(login));
    return () => logins.forEach((login) => void store.unwatchAccount(login));
  }, [store, key]);

  return <StreamContext.Provider value={store}>{children}</StreamContext.Provider>;
}
